<template>
    <DForm @submit.prevent="submitForm">
        <DFormRow
            :label="__('products.fulfillmentAccounts.form.accountGroup')"
            :is-required="true"
            :error="errors.group_id"
            :hint="__('products.fulfillmentAccounts.form.accountGroup_hint')"
        >
            <DSelect
                id="fulfillment_account_group"
                v-model="form.group_id"
                :options="accountGroups"
                :placeholder="__('products.fulfillmentAccounts.form.accountGroup')"
                :is-valid="isFormFieldValid('group_id')"
                @update:modelValue="onGroupChange"
            />
        </DFormRow>
        <DFormRow
            :label="__('products.fulfillmentAccounts.form.name')"
            :is-required="true"
            :error="errors.name"
            :hint="__('products.fulfillmentAccounts.form.name_hint')"
        >
            <DInput
                id="name"
                v-model="form.name"
                :placeholder="__('products.fulfillmentAccounts.form.name')"
                :is-valid="isFormFieldValid('name')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('products.fulfillmentAccounts.form.hostname')"
            :error="errors.hostname"
            :hint="__('products.fulfillmentAccounts.form.hostname_hint')"
        >
            <DInput
                id="hostname"
                v-model="form.hostname"
                :placeholder="__('products.fulfillmentAccounts.form.hostname')"
                :is-valid="isFormFieldValid('hostname')"
                maxlength="128"
            />
        </DFormRow>
        <DFormRow
            :label="__('products.fulfillmentAccounts.form.username')"
            :error="errors.username"
            :hint="__('products.fulfillmentAccounts.form.username_hint')"
        >
            <DInput
                id="username"
                v-model="form.username"
                :placeholder="__('products.fulfillmentAccounts.form.username')"
                :is-valid="isFormFieldValid('username')"
                maxlength="64"
            />
        </DFormRow>
        <DFormRow
            :label="__('products.fulfillmentAccounts.form.password')"
            :error="errors.password"
            :hint="__('products.fulfillmentAccounts.form.password_hint')"
        >
            <DInput
                id="password"
                v-model="form.password"
                type="password"
                :placeholder="__('products.fulfillmentAccounts.form.password')"
                :is-valid="isFormFieldValid('password')"
            />
        </DFormRow>
        <DFormRow
            :label="__('products.fulfillmentGroups.form.tag')"
            :error="errors.tags"
            :hint="__('products.fulfillmentGroups.form.tag_hint')"
        >
            <DMultiSelect
                id="account_tags"
                :options="selectedAccountTagOptions()"
                :selection="true"
                selection-type="tags"
                :search="true"
                @update="updateSelectedAccountTags"
            />
        </DFormRow>
        <DFormRow
            label=""
            :is-required="true"
            :error="errors.group_id"
        >
            <div style="display: flex; grid-gap: 15px">
                <div
                    v-if="checkSuccessful === true"
                    style="color: var(--colorSuccess);"
                >
                    {{ __('products.fulfillmentAccounts.responses.success.check') }}
                </div>
                <div
                    v-else-if="checkSuccessful === false"
                    style="color: var(--colorDanger);"
                >
                    {{ checkMessage !== null ? checkMessage : __('products.fulfillmentAccounts.responses.error.check') }}
                </div>
                <DButton
                    icon="lan-connect"
                    :label="__('products.fulfillmentAccounts.form.test_account')"
                    :disabled="isInLoadingState"
                    @click="testAccount"
                />
            </div>
        </DFormRow>
        <DFormRow
            :label="__('products.fulfillmentAccounts.form.status')"
            :is-required="true"
            :error="errors.status"
            :hint="__('products.fulfillmentAccounts.form.status_hint')"
        >
            <DSelect
                id="status"
                v-model="form.status"
                :options="accountStatuses"
                :placeholder="__('products.fulfillmentAccounts.form.status')"
                :is-valid="isFormFieldValid('status')"
            />
        </DFormRow>
        <DFormRow
            :label="__('products.fulfillmentAccounts.form.maxInstances')"
            :is-required="true"
            :error="errors.max_instances"
            :hint="__('products.fulfillmentAccounts.form.maxInstances_hint')"
        >
            <DInput
                id="max_instances"
                v-model="form.max_instances"
                :placeholder="__('products.fulfillmentAccounts.form.maxInstances')"
                :is-valid="isFormFieldValid('max_instances')"
                type="number"
                min="1"
                max="2147483647"
            />
        </DFormRow>
        <DFormRow
            :label="__('products.fulfillmentAccounts.form.additionalData')"
            :hint="__('products.fulfillmentAccounts.form.additionalData_hint')"
        >
            <DFormTextarea
                id="additional_data"
                v-model="form.additional_data"
                :placeholder="__('products.fulfillmentAccounts.form.additionalData')"
                :is-valid="isAdditionalDataValid()"
                class="mb-0"
            />
            <div
                v-if="isAdditionalDataValid() === false"
                class="invalid-feedback d-block"
            >
                <div
                    v-for="(additionalDataError, index) in additionalDataErrors"
                    :key="index"
                >
                    {{ additionalDataError }}
                </div>
            </div>
        </DFormRow>

        <template v-if="accountOptions.length">
            <h5 class="mt-4">
                {{ __('products.fulfillmentAccountOptions.section_title') }}
            </h5>
            <AccountOptionFields
                :options="accountOptions"
                :values="form.account_options"
                :errors="errors"
                :test-route="effectiveTestRoute"
                tier="account"
            />
        </template>

        <DFormControls
            :cancel-route="$route('products.accounts.index')"
            @submit="submitForm"
        />
    </DForm>
</template>

<script>
import _ from 'lodash'
import { router } from '@inertiajs/vue3'
import AccountOptionFields from '~@/Components/Fulfillment/AccountOptions/AccountOptionFields'

export default {
    name: 'FulfillmentAccountForm',
    components: {
        AccountOptionFields
    },
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        account: {
            type: Object,
            default: () => { return {} }
        },
        accountGroups: {
            type: Array,
            default: () => { return [] }
        },
        accountTags: {
            type: Array,
            default: () => { return [] }
        },
        tags: {
            type: Array,
            default: () => { return [] }
        },
        accountStatuses: {
            type: Array,
            default: () => { return [] }
        },
        checkSuccessful: {
            type: Boolean,
            default: () => { return null }
        },
        checkMessage: {
            type: String,
            default: () => { return null }
        },
        accountGroupDataTemplates: {
            type: Array,
            default () {
                return []
            }
        },
        accountOptions: {
            type: Array,
            default: () => { return [] }
        },
        testRoute: {
            type: String,
            default: null
        }
    },
    emits: ['form-submitted', 'test-account'],
    data () {
        return {
            form: this.$inertia.form({
                group_id: this.account?.group_id ?? null,
                name: this.account?.name ?? null,
                hostname: this.account?.hostname ?? null,
                username: this.account?.username ?? null,
                password: this.account?.password ?? null,
                status: this.account?.status ?? null,
                max_instances: this.account?.max_instances ?? null,
                additional_data: JSON.stringify(this.account?.data, null, 6) ?? null,
                additional_data_parsed: null,
                tags: Array.isArray(this.tags)
                    ? this.tags.map(tag => tag.value)
                    : [],
                account_options: this.buildAccountOptionValues(),
            })
        }
    },
    computed: {
        additionalDataErrors () {
            const errorsList = []

            Object.keys(this.errors).forEach((key) => {
                if (key.startsWith('additional_data')) {
                    errorsList.push(this.errors[key])
                }
            })

            return errorsList
        },
        // Edit tests against the account; create tests against the selected
        // group (no account exists yet), reactively as the group changes.
        effectiveTestRoute () {
            if (this.testRoute) {
                return this.testRoute
            }

            if (this.form.group_id) {
                return this.$route('products.account-groups.options.test-connection', { accountGroup: this.form.group_id })
            }

            return null
        }
    },
    watch: {
        // The server re-resolves the options for the selected group on reload;
        // rebuild the bound values so the fields reflect the new module/group.
        accountOptions () {
            this.form.account_options = this.buildAccountOptionValues()
        }
    },
    methods: {
        buildAccountOptionValues () {
            const values = {}

            this.accountOptions.forEach((option) => {
                values[option.name] = option.encrypted ? '' : (option.value ?? '')
            })

            return values
        },
        onGroupChange (groupId) {
            this.updateDataTemplates(groupId)

            router.reload({
                only: ['accountOptions'],
                data: { group_id: groupId },
                preserveState: true,
                preserveScroll: true,
            })
        },
        submitForm () {
            if (this.form.additional_data !== null && this.form.additional_data.length !== 0) {
                try {
                    this.form.additional_data_parsed = JSON.parse(this.form.additional_data)
                } catch (e) {
                    this.form.additional_data_parsed = {}
                }
            }
            this.$emit('form-submitted', this.form)
        },
        isAdditionalDataValid () {
            const errorsKeys = Object.keys(this.errors)

            if (errorsKeys.length === 0) {
                return null
            }

            const additionalDataErrors = errorsKeys.filter((key) => {
                return key.startsWith('additional_data')
            })

            return additionalDataErrors.length === 0
        },
        testAccount () {
            if (this.form.additional_data !== null && this.form.additional_data.length !== 0) {
                try {
                    this.form.additional_data_parsed = JSON.parse(this.form.additional_data)
                } catch (e) {
                    this.form.additional_data_parsed = {}
                }
            }

            this.$emit('test-account', this.form)
        },
        updateDataTemplates (accountGroup) {
            if (_.isEmpty(this.account)) {
                const template = this.accountGroupDataTemplates.filter(item => item.id === parseInt(accountGroup))[0]

                this.form.additional_data = template && template?.data_template !== null ? JSON.stringify(template.data_template, null, 6) : ''
            }
        },
        updateSelectedAccountTags (values) {
            this.form.tags = values
        },
        selectedAccountTagOptions () {
            if (this.tags.length > 0) {
                const selectedTags = this.tags.map(tag => tag.value)
                return this.accountTags
                    .map((option) => ({ ...option, selected: selectedTags.includes(option.value) }))
            }

            return this.accountTags
        },
    }
}
</script>
